import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";

const QuoteContext = createContext(null);

const STORAGE_KEY = "nsp_quote_items_v1";

/* Se la bozza è vecchia e le date di ritiro sono passate, sposta il periodo
   in avanti (ritiro domani) mantenendo la stessa durata. */
const sanitizeStaleDates = (items) => {
    if (!Array.isArray(items)) return { items: [], updated: false };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let updated = false;
    const next = items.map((it) => {
        if (!it?.pickup_date) return it;
        const pickup = new Date(it.pickup_date);
        pickup.setHours(0, 0, 0, 0);
        if (pickup >= today) return it;
        updated = true;
        const days = Math.max(1, it.days || 1);
        const newPickup = new Date(today);
        newPickup.setDate(today.getDate() + 1);
        const newReturn = new Date(newPickup);
        newReturn.setDate(newPickup.getDate() + days);
        return {
            ...it,
            pickup_date: newPickup.toISOString(),
            return_date: newReturn.toISOString(),
        };
    });
    return { items: next, updated };
};

/* Provider globale per il preventivo: gli articoli persistono tra le pagine
   e tra i refresh (localStorage). */
export const QuoteProvider = ({ children }) => {
    const staleNoticeRef = React.useRef(false);
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            const parsed = raw ? JSON.parse(raw) : [];
            const { items: sanitized, updated } = sanitizeStaleDates(parsed);
            staleNoticeRef.current = updated;
            return sanitized;
        } catch (_) {
            return [];
        }
    });

    useEffect(() => {
        if (staleNoticeRef.current) {
            staleNoticeRef.current = false;
            toast.info(
                "Abbiamo aggiornato le date del tuo preventivo: il periodo salvato era passato, ora parte da domani con la stessa durata.",
                { duration: 6000 }
            );
        }
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        } catch (_) {
            /* ignore */
        }
    }, [items]);

    const addToQuote = useCallback((product) => {
        setItems((prev) => {
            if (prev.find((q) => q.product.id === product.id)) {
                toast.info(`${product.name} è già nel preventivo`);
                return prev;
            }
            toast.success(`${product.name} aggiunto al preventivo`);
            const today = new Date();
            const pickup = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
            const ret = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 2);
            return [
                ...prev,
                {
                    product,
                    pickup_date: pickup.toISOString(),
                    return_date: ret.toISOString(),
                    days: 1,
                    quantity: 1,
                },
            ];
        });
    }, []);

    const selectedIds = new Set(items.map((q) => q.product.id));

    /* Applica un'azione preventivo proveniente dal chatbot. */
    const applyChatAction = useCallback((a) => {
        setItems((prev) => {
            if (a.action === "clear") {
                if (prev.length) toast.info("Preventivo svuotato dall'assistente");
                return [];
            }
            if (a.action === "remove") {
                const found = prev.find((q) => q.product.id === a.product_id);
                if (found) toast.info(`${found.product.name} rimosso dal preventivo`);
                return prev.filter((q) => q.product.id !== a.product_id);
            }
            if (a.action === "update") {
                return prev.map((q) => {
                    if (q.product.id !== a.product_id) return q;
                    const days = a.days ?? q.days;
                    const pickup = new Date(q.pickup_date);
                    const ret = new Date(pickup);
                    ret.setDate(pickup.getDate() + days);
                    toast.success(`${q.product.name} aggiornato (${days} gg x${a.quantity ?? q.quantity})`);
                    return {
                        ...q,
                        days,
                        quantity: a.quantity ?? q.quantity,
                        return_date: ret.toISOString(),
                    };
                });
            }
            if (a.action === "add" && a.product) {
                const days = a.days || 1;
                const quantity = a.quantity || 1;
                const today = new Date();
                const pickup = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
                const ret = new Date(pickup);
                ret.setDate(pickup.getDate() + days);
                const existing = prev.find((q) => q.product.id === a.product.id);
                if (existing) {
                    toast.success(`${a.product.name} aggiornato nel preventivo`);
                    return prev.map((q) =>
                        q.product.id === a.product.id
                            ? { ...q, days, quantity, pickup_date: pickup.toISOString(), return_date: ret.toISOString() }
                            : q
                    );
                }
                toast.success(`${a.product.name} aggiunto al preventivo`);
                return [
                    ...prev,
                    {
                        product: a.product,
                        pickup_date: pickup.toISOString(),
                        return_date: ret.toISOString(),
                        days,
                        quantity,
                    },
                ];
            }
            return prev;
        });
    }, []);

    return (
        <QuoteContext.Provider value={{ items, setItems, addToQuote, selectedIds, applyChatAction }}>
            {children}
        </QuoteContext.Provider>
    );
};

export const useQuote = () => {
    const ctx = useContext(QuoteContext);
    if (!ctx) throw new Error("useQuote must be used within QuoteProvider");
    return ctx;
};
